import { TabledRepeat } from "lib/@components/tabled-repeat/tabled-repeat"
import { AddGroupScheduleRow } from "../add/add-group-schedule-row"
import { Checkbox, Stack, Typography } from "@mui/material"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useEffect, useMemo, useState } from "react"
import { ResetTabledRepeat } from "event-definitions"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { omit } from "lib/omit"
import { useDialog } from "lib/@hooks/useDialog"
import { AuthoringDeleteDialog } from "../../plugins/project-item/authoring-delete-dialog"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { useParams } from "react-router"
import { getDraftOrPublishedSchedules } from "authoring/routes/project/controller/get-draft-or-published-schedules"
import { refreshOnlyFor } from "lib/graphql/api"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { compareByCode } from "authoring/lib/compare-by-code"
import { SelectedItemsBanner } from "../../components/selected-items-banner"
import { updateProjectGroup } from "../controller/update-project-group"
import { getExpandedSchedulePreviews } from "../../schedule-editor/controller/get-expanded-schedule-previews"
import { If } from "lib/@components/switch"
import {
    buildExpandedPreviewLookup,
    buildExpandedPreviewScheduleList,
} from "../lib/build-expanded-preview-schedule-list"
import { stripTypename } from "../lib/strip-typename"
import { useDemand } from "lib/authorization/use-demand"
import { AUTHORING_EDIT_SCHEDULES_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"

export function IntroductionGroupSchedules() {
    const { projectId, groupId } = useParams()
    const { projectGroup, groupLoading } = useBoundContext()
    const canEditSchedule = useDemand(AUTHORING_EDIT_SCHEDULES_DEMAND)
    const [selectedSchedules, setSelectedSchedules] = useState({})
    const selectedScheduleIds = Object.keys(selectedSchedules)
    const scheduleIds = (projectGroup?.introTypeSchedules ?? []).map("id")
    const projectFilter = refreshOnlyFor(projectId)
    const schedulesResult = getDraftOrPublishedSchedules.useResults(
        { projectId, scheduleIds },
        projectFilter,
        withInitialValue(IS_LOADING)
    )
    const schedulesLoading = schedulesResult === IS_LOADING
    const schedules = schedulesLoading ? undefined : schedulesResult
    const expandedPreviewItems = getExpandedSchedulePreviews.useResults({ projectId, scheduleIds }, projectFilter)
    const [searchParam] = useSearchParam("search", "")
    const search = searchParam.toLowerCase()
    const expandedPreviewLookup = useMemo(
        () => buildExpandedPreviewLookup(expandedPreviewItems),
        [expandedPreviewItems]
    )
    const scheduleList = useMemo(
        () => buildExpandedPreviewScheduleList(schedules, expandedPreviewLookup),
        [schedules, expandedPreviewLookup]
    )
    const filteredSchedules = useMemo(
        () => scheduleList.filter(matchesSearch).sort(compareByCode),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [scheduleList, search]
    )
    const scheduleString = selectedScheduleIds?.length > 1 ? "schedules" : "schedule"
    const allSelected = filteredSchedules?.length > 0 && selectedScheduleIds?.length === filteredSchedules?.length
    const someSelected = selectedScheduleIds?.length > 0 && !allSelected
    const checkboxHeader = canEditSchedule ? (
        <Checkbox
            indeterminate={someSelected}
            checked={allSelected}
            onChange={handleToggleAll}
            disabled={projectGroup?.readOnly}
        />
    ) : null
    const tableHeaders = [
        {
            title: checkboxHeader,
        },
        { title: "Name" },
        { title: "Code" },
        { title: "Tasks", align: "right" },
        { title: "" },
    ]

    const deleteSchedulesDialog = useDialog(
        <AuthoringDeleteDialog
            value={true}
            title={`Delete ${selectedScheduleIds.length > 1 ? selectedScheduleIds.length : ""} ${scheduleString.titleize()}`}
            message={`This action cannot be undone, but the ${scheduleString} can be re-added manually`}
        />
    )

    useEffect(() => {
        ResetTabledRepeat.raiseOnce()
    }, [searchParam, selectedSchedules, scheduleList])

    useEffect(() => {
        if (!canEditSchedule && Object.keys(selectedSchedules).length) setSelectedSchedules({})
    }, [canEditSchedule, selectedSchedules])

    if (groupLoading || schedulesLoading) return <LoadingScreen />

    return (
        <Stack spacing={2}>
            <Typography sx={{ fontWeight: "medium" }}>Schedules</Typography>
            <SearchBoxUsingSearchParam />
            <If value={canEditSchedule}>
                <SelectedItemsBanner selected={selectedScheduleIds} label={"schedule"} handleDelete={handleDelete} />
            </If>
            <TabledRepeat
                tableHeaders={tableHeaders}
                itemPass="schedule"
                items={filteredSchedules}
                itemComponent={<AddGroupScheduleRow selected={selectedSchedules} handleSelect={handleSelect} />}
                alternating={false}
                paged={true}
                headerStyles={{ backgroundColor: "background.paper" }}
            />
        </Stack>
    )

    function matchesSearch(schedule) {
        const title = schedule?.title ?? ""
        const code = schedule?.code ?? ""
        return title.toLowerCase().includes(search) || code.toLowerCase().includes(search)
    }
    function handleToggleAll() {
        if (allSelected || someSelected) return setSelectedSchedules({})
        const newSelection = {}
        for (const { id } of filteredSchedules) newSelection[id] = true
        return setSelectedSchedules(newSelection)
    }

    function handleSelect(scheduleId) {
        const deselect = Boolean(selectedSchedules[scheduleId])
        if (deselect) {
            setSelectedSchedules((selected) => omit(selected, scheduleId))
        } else {
            setSelectedSchedules((selected) => ({ ...selected, [scheduleId]: true }))
        }
    }

    async function handleDelete(scheduleIds) {
        try {
            const confirm = await deleteSchedulesDialog()
            if (!confirm) return

            const remaining = (projectGroup?.introTypeSchedules ?? [])
                .filter((s) => !scheduleIds.includes(s.id))
                .map(stripTypename)
            const deleted = await updateProjectGroup({
                projectId,
                group: { id: groupId, introTypeSchedules: remaining },
            })
            if (!deleted) {
                errorSnackbar(`Failed to delete ${scheduleString} from group`)
                return
            }

            setSelectedSchedules({})
            successSnackbar(`Successfully deleted ${scheduleString}`)
        } catch (error) {
            console.error(error)
            errorSnackbar(`Unexpected error while deleting ${scheduleString}`)
        }
    }
}
